import React, { useState, useEffect, useRef } from 'react';
import { api, timeAgo, uploadMedia, deleteUploadedMedia, resolveMediaUrl, getVideoDuration } from '../lib/api';
import { useApp } from '../context/AppContext';
import { useNavigate } from 'react-router-dom';
import { Avatar, AvatarImage, AvatarFallback } from '../components/ui/avatar';
import { VerifiedBadge } from '../components/app/VerifiedBadge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Progress } from '../components/ui/progress';
import { Heart, MessageCircle, Share2, Volume2, VolumeX, Play, ExternalLink, Plus, Video, X, Trash2 } from 'lucide-react';
import { toast } from 'sonner';

const MAX_REEL_SECONDS = 180; // 3 minutes

function CreateReelDialog({ open, onOpenChange, onCreated, t }) {
  const [file, setFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [duration, setDuration] = useState(0);
  const [caption, setCaption] = useState('');
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState(0);
  const fileRef = useRef();

  const onFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!f.type.startsWith('video/')) { toast.error(t('selectVideo')); return; }
    const dur = await getVideoDuration(f);
    if (dur > MAX_REEL_SECONDS) {
      toast.error(`Reels are limited to 3 minutes. Your video is ${Math.floor(dur / 60)}:${String(Math.floor(dur % 60)).padStart(2, '0')}.`);
      e.target.value = '';
      return;
    }
    setDuration(dur);
    setFile(f);
    setPreviewUrl(URL.createObjectURL(f));
    e.target.value = '';
  };

  const reset = () => {
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setFile(null); setPreviewUrl(null); setDuration(0); setProgress(0);
  };

  const publish = async () => {
    if (!file) { toast.error(t('selectVideoFirst')); return; }
    setBusy(true);
    setProgress(0);
    let uploadedUrl = '';
    try {
      const media = await uploadMedia(file, setProgress);
      uploadedUrl = media.url;
      const res = await api.post('/reels', { video_url: media.url, caption });
      toast.success(`${t('reelPublished')} · +15 XP`);
      onCreated?.(res.data);
      reset(); setCaption('');
      onOpenChange(false);
    } catch (err) {
      if (uploadedUrl) await deleteUploadedMedia(uploadedUrl).catch(() => {});
      toast.error(err.response?.data?.detail || t('failedPublish'));
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!busy) onOpenChange(v); }}>
      <DialogContent className="max-w-sm" data-testid="create-reel-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Video size={18} className="text-primary" /> {t('newReel')}</DialogTitle>
          <DialogDescription>{t('uploadVerticalVideo')}</DialogDescription>
        </DialogHeader>
        <input ref={fileRef} type="file" accept="video/*" hidden onChange={onFile} />
        {!file ? (
          <button
            data-testid="reel-select-video-button"
            onClick={() => fileRef.current?.click()}
            className="border-2 border-dashed border-primary/30 rounded-xl p-8 text-center hover:border-primary/60 transition-colors"
          >
            <Video size={30} className="mx-auto mb-2 text-primary/60" />
            <p className="text-sm text-muted-foreground">{t('tapSelectVideo')}</p>
            <p className="text-[11px] text-muted-foreground mt-1">{t('maxDuration')}</p>
          </button>
        ) : (
          <div className="space-y-3">
            <div className="relative rounded-xl overflow-hidden bg-black">
              <video src={previewUrl} className="w-full max-h-64 object-contain" controls muted playsInline />
              {!busy && (
                <button
                  onClick={reset}
                  className="absolute top-2 end-2 w-7 h-7 rounded-full bg-black/60 flex items-center justify-center text-white"
                >
                  <X size={14} />
                </button>
              )}
            </div>
            <p className="text-[11px] text-muted-foreground truncate">
              {file.name} · {(file.size / (1024 * 1024)).toFixed(1)}MB · {Math.floor(duration / 60)}:{String(Math.floor(duration % 60)).padStart(2, '0')}
            </p>
            {busy && (
              <div className="space-y-1">
                <Progress value={progress} className="h-2" />
                <p className="text-[11px] text-muted-foreground text-center">
                   {progress < 100 ? `${t('uploading')} ${progress}%` : t('processing')}
                </p>
              </div>
            )}
          </div>
        )}
        <Input
          data-testid="reel-caption-input"
          value={caption}
          onChange={(e) => setCaption(e.target.value)}
          placeholder={t('caption')}
          maxLength={150}
          disabled={busy}
        />
        <Button data-testid="reel-publish-button" onClick={publish} disabled={busy || !file} className="w-full font-semibold">
          {busy ? `${progress}%` : t('publish')}
        </Button>
      </DialogContent>
    </Dialog>
  );
}

function ReelItem({ reel, isAd, t, navigate, user, onDeleted }) {
  const videoRef = useRef();
  const [playing, setPlaying] = useState(false);
  const [muted, setMuted] = useState(true);
  const [liked, setLiked] = useState(reel?.liked_by_me);
  const [likeCount, setLikeCount] = useState(reel?.like_count || 0);
  const [commentCount, setCommentCount] = useState(reel?.comment_count || 0);
  const [commentsOpen, setCommentsOpen] = useState(false);
  const [comments, setComments] = useState([]);
  const [commentText, setCommentText] = useState('');
  const [commentsLoading, setCommentsLoading] = useState(false);
  const [shares, setShares] = useState(reel?.shares || 0);
  const [views, setViews] = useState(reel?.views || 0);
  const impressionSent = useRef(false);
  const viewSent = useRef(false);

  useEffect(() => {
    const el = videoRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          el.play().then(() => setPlaying(true)).catch(() => setPlaying(false));
          if (isAd && !impressionSent.current && reel?.id) {
            impressionSent.current = true;
            api.post(`/ads/${reel.id}/impression`).catch(() => {});
          } else if (!isAd && !viewSent.current && reel?.id) {
            viewSent.current = true;
            api.post(`/reels/${reel.id}/view`).then((response) => setViews(response.data.views)).catch(() => {});
          }
        } else {
          el.pause();
          setPlaying(false);
        }
      },
      { threshold: 0.6 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [isAd, reel?.id]);

  const togglePlay = () => {
    const el = videoRef.current;
    if (!el) return;
    if (el.paused) { el.play(); setPlaying(true); } else { el.pause(); setPlaying(false); }
  };

  const toggleLike = async () => {
    if (isAd) return;
    setLiked(!liked); setLikeCount((c) => (liked ? c - 1 : c + 1));
    try {
      const res = await api.post(`/reels/${reel.id}/like`);
      setLiked(res.data.liked); setLikeCount(res.data.like_count);
    } catch {}
  };

  const openComments = async () => {
    setCommentsOpen(true);
    setCommentsLoading(true);
    try {
      const response = await api.get(`/reels/${reel.id}/comments`);
      setComments(response.data);
    } catch {
      toast.error(t('connectionError'));
    } finally {
      setCommentsLoading(false);
    }
  };

  const addComment = async () => {
    const value = commentText.trim();
    if (!value) return;
    try {
      const response = await api.post(`/reels/${reel.id}/comments`, { text: value });
      setComments((current) => [...current, response.data]);
      setCommentCount((current) => current + 1);
      setCommentText('');
    } catch {
      toast.error(t('connectionError'));
    }
  };

  const shareReel = async () => {
    try {
      const response = await api.post(`/reels/${reel.id}/share`);
      setShares(response.data.shares);
      const url = `${window.location.origin}/reels?reel=${reel.id}`;
      if (navigator.share) await navigator.share({ title: reel.caption || 'FitCore reel', url });
      else await navigator.clipboard?.writeText(url);
      toast.success(t('linkCopied'));
    } catch (error) {
      if (error?.name !== 'AbortError') toast.error(t('connectionError'));
    }
  };

  const deleteReel = async () => {
    if (!window.confirm(t('deleteReelConfirm'))) return;
    try {
      await api.delete(`/reels/${reel.id}`);
      onDeleted(reel.id);
      toast.success(t('reelDeleted'));
    } catch (error) {
      toast.error(error.response?.data?.detail || t('connectionError'));
    }
  };

  if (isAd) {
    return (
      <div className="relative h-[calc(100vh-56px)] lg:h-screen w-full flex items-center justify-center bg-black">
        <img src={reel.image} alt={reel.title} className="absolute inset-0 w-full h-full object-cover opacity-80" />
        <div className="absolute inset-0 reels-vignette" />
        <span data-testid="reel-sponsored-label" className="absolute top-5 start-4 text-[10px] uppercase tracking-wider text-white/90 border border-white/40 rounded-full px-2 py-0.5 z-10">
          {t('sponsored')} · {reel.sponsor}
        </span>
        <div className="absolute bottom-20 inset-x-0 px-6 z-10 text-center">
          <h3 className="text-white text-xl font-semibold mb-2">{reel.title}</h3>
          <p className="text-white/70 text-sm mb-4">{reel.description}</p>
          <button onClick={() => api.post(`/ads/${reel.id}/click`).catch(() => {})}
            className="px-6 py-2.5 rounded-full bg-[hsl(205,100%,56%)] text-black font-semibold text-sm inline-flex items-center gap-2">
            {reel.cta} <ExternalLink size={14} />
          </button>
        </div>
      </div>
    );
  }

  return (
    <>
    <div className="relative h-[calc(100vh-56px)] lg:h-screen w-full bg-black flex items-center justify-center" data-testid="reel-item">
      <video
        ref={videoRef}
        src={resolveMediaUrl(reel.video_url)}
        className="h-full w-full object-contain"
        loop
        muted={muted}
        playsInline
        onClick={togglePlay}
      />
      {!playing && (
        <button onClick={togglePlay} className="absolute inset-0 flex items-center justify-center z-10">
          <div className="w-16 h-16 rounded-full bg-black/50 flex items-center justify-center">
            <Play size={28} className="text-white ms-1" />
          </div>
        </button>
      )}
      <div className="absolute inset-x-0 bottom-0 h-1/3 reels-vignette pointer-events-none" />
      {/* info */}
      <div className="absolute bottom-6 start-4 end-20 z-10">
        <div className="flex items-center gap-2 mb-2 cursor-pointer" onClick={() => navigate(`/profile/${reel.author?.id}`)}>
          <Avatar className="w-9 h-9 ring-1 ring-white/40">
            <AvatarImage src={resolveMediaUrl(reel.author?.avatar)} />
            <AvatarFallback>{reel.author?.username?.[0]}</AvatarFallback>
          </Avatar>
          <span className="text-white text-sm font-semibold">@{reel.author?.username}</span>
          {reel.author?.verified && <VerifiedBadge size={14} />}
          <span className="text-white/50 text-xs">{timeAgo(reel.created_at)}</span>
        </div>
        <p className="text-white/90 text-sm">{reel.caption}</p>
        <p className="text-white/50 text-xs mt-1">{views.toLocaleString()} {t('views')}</p>
      </div>
      {/* actions */}
      <div className="absolute bottom-8 end-3 z-10 flex flex-col items-center gap-5">
        <button data-testid="reel-like-button" onClick={toggleLike} className="flex flex-col items-center gap-1">
          <div className={`w-11 h-11 rounded-full bg-black/40 flex items-center justify-center ${liked ? 'text-primary' : 'text-white'}`}>
            <Heart size={22} fill={liked ? 'currentColor' : 'none'} />
          </div>
          <span className="text-white text-xs">{likeCount}</span>
        </button>
        <button data-testid="reel-comment-button" onClick={openComments} className="flex flex-col items-center gap-1">
          <div className="w-11 h-11 rounded-full bg-black/40 flex items-center justify-center text-white">
            <MessageCircle size={22} />
          </div>
          <span className="text-white text-xs">{commentCount}</span>
        </button>
        <button data-testid="reel-share-button" onClick={shareReel}
          className="flex flex-col items-center gap-1">
          <div className="w-11 h-11 rounded-full bg-black/40 flex items-center justify-center text-white">
            <Share2 size={22} />
          </div>
          {shares > 0 && <span className="text-white text-xs">{shares}</span>}
        </button>
        <button data-testid="reel-mute-button" onClick={() => setMuted(!muted)} className="w-11 h-11 rounded-full bg-black/40 flex items-center justify-center text-white">
          {muted ? <VolumeX size={20} /> : <Volume2 size={20} />}
        </button>
        {user?.id === reel.user_id && (
          <button data-testid="reel-delete-button" onClick={deleteReel} className="w-11 h-11 rounded-full bg-black/40 flex items-center justify-center text-white">
            <Trash2 size={20} />
          </button>
        )}
      </div>
    </div>
    <Dialog open={commentsOpen} onOpenChange={setCommentsOpen}>
      <DialogContent className="max-w-md max-h-[80vh] flex flex-col" data-testid="reel-comments-dialog">
        <DialogHeader><DialogTitle>{t('comments')}</DialogTitle></DialogHeader>
        <div className="min-h-40 flex-1 overflow-y-auto space-y-4 pe-1">
          {commentsLoading ? (
            <p className="py-10 text-center text-sm text-muted-foreground">{t('loading')}</p>
          ) : comments.length === 0 ? (
            <p className="py-10 text-center text-sm text-muted-foreground">{t('noCommentsYet')}</p>
          ) : comments.map((comment) => (
            <div key={comment.id} className="flex gap-3">
              <Avatar className="w-8 h-8 shrink-0">
                <AvatarImage src={resolveMediaUrl(comment.author?.avatar)} />
                <AvatarFallback>{comment.author?.username?.[0]}</AvatarFallback>
              </Avatar>
              <div className="min-w-0">
                <p className="text-sm"><span className="font-semibold me-2">@{comment.author?.username}</span>{comment.text}</p>
                <p className="text-[11px] text-muted-foreground mt-1">{timeAgo(comment.created_at)}</p>
              </div>
            </div>
          ))}
        </div>
        <div className="flex gap-2 border-t border-border pt-3">
          <Input data-testid="reel-comment-input" value={commentText} onChange={(event) => setCommentText(event.target.value)}
            placeholder={t('addComment')} onKeyDown={(event) => event.key === 'Enter' && addComment()} />
          <Button data-testid="reel-comment-submit" onClick={addComment} disabled={!commentText.trim()}><MessageCircle size={16} /></Button>
        </div>
      </DialogContent>
    </Dialog>
    </>
  );
}

export default function ReelsPage() {
  const { t, user } = useApp();
  const navigate = useNavigate();
  const [reels, setReels] = useState([]);
  const [ads, setAds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [error, setError] = useState('');
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    setLoading(true);
    setError('');
    Promise.all([api.get('/reels'), api.get('/ads?placement=reel')])
      .then(([r, a]) => { setReels(r.data); setAds(a.data); })
      .catch(() => setError(t('connectionError')))
      .finally(() => setLoading(false));
  }, [reloadKey, t]);

  const onCreated = (reel) => setReels((prev) => [reel, ...prev]);

  // interleave: ad after every 3 reels
  const items = [];
  reels.forEach((reel, i) => {
    items.push({ type: 'reel', data: reel, key: reel.id });
    if ((i + 1) % 3 === 0 && ads.length > 0) {
      items.push({ type: 'ad', data: ads[Math.floor(i / 3) % ads.length], key: `ad-${i}` });
    }
  });

  if (loading) {
    return (
      <div className="h-screen flex items-center justify-center bg-black">
        <div className="w-10 h-10 border-2 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="relative">
      {/* Create reel floating button */}
      <button
        data-testid="reel-create-button"
        onClick={() => setCreateOpen(true)}
        className="fixed top-4 end-4 lg:end-8 z-50 flex items-center gap-1.5 px-4 h-10 rounded-full bg-primary text-primary-foreground font-semibold text-sm shadow-lg glow-fitness active:scale-95 transition-transform"
      >
        <Plus size={17} /> {t('newReel')}
      </button>
      <CreateReelDialog open={createOpen} onOpenChange={setCreateOpen} onCreated={onCreated} t={t} />
      <div className="h-[calc(100vh-56px)] lg:h-screen overflow-y-auto reels-snap bg-black" data-testid="reels-viewer">
        {error && (
          <div className="h-full flex flex-col items-center justify-center text-white/70 gap-4 px-6 text-center">
            <p className="text-sm">{error}</p>
            <Button onClick={() => setReloadKey((value) => value + 1)}>{t('retry')}</Button>
          </div>
        )}
        {!error && items.map((item) => (
          <ReelItem key={item.key} reel={item.data} isAd={item.type === 'ad'} t={t} navigate={navigate} user={user} onDeleted={(reelId) => setReels((current) => current.filter((reel) => reel.id !== reelId))} />
        ))}
        {!error && items.length === 0 && (
          <div className="h-full flex flex-col items-center justify-center text-muted-foreground gap-3">
            <Video size={36} className="opacity-40" />
            <p className="text-sm">{t('noReelsYet')}</p>
          </div>
        )}
      </div>
    </div>
  );
}
